import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard, TrendingUp, MessageSquare, FolderOpen, Bell,
  Sparkles, CreditCard, Settings, LogOut, Menu, X,
} from "lucide-react";
import { useClientAuth } from "@/context/ClientAuthContext";

const NAV = [
  { to: "/client/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/client/progress", label: "Progress", icon: TrendingUp },
  { to: "/client/messages", label: "Messages", icon: MessageSquare },
  { to: "/client/documents", label: "Documents", icon: FolderOpen },
  { to: "/client/notifications", label: "Notifications", icon: Bell },
  { to: "/client/advisor", label: "AI Advisor", icon: Sparkles },
  { to: "/client/billing", label: "Billing & Invoices", icon: CreditCard },
  { to: "/client/settings", label: "Profile & Settings", icon: Settings },
];

function SidebarLink({ to, label, icon: Icon, onClick }) {
  return (
    <NavLink to={to} onClick={onClick} data-testid={`client-nav-${label.toLowerCase().split(" ")[0]}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm transition-colors ${
          isActive ? "bg-[#D4AF37]/12 text-[#D4AF37] border border-[#D4AF37]/20"
                    : "text-[#A1A1AA] hover:text-white hover:bg-white/5 border border-transparent"}`}>
      <Icon className="w-[18px] h-[18px] shrink-0" /> {label}
    </NavLink>
  );
}

export default function ClientLayout() {
  const { client, logout } = useClientAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => { setOpen(false); }, [navigate]);

  const doLogout = async () => { await logout(); navigate("/client/login"); };

  const brand = (
    <div className="flex items-center gap-2.5 px-2">
      <div className="w-10 h-10 rounded-lg fv-gold-gradient flex items-center justify-center">
        <span className="font-heading text-[#0A0A0B] text-2xl font-bold">F</span>
      </div>
      <div><div className="font-heading text-lg text-white leading-none">Fairview</div>
        <div className="text-[9px] tracking-[0.22em] uppercase text-[#D4AF37]">Client Portal</div></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0B] flex fv-grain">
      <aside className="hidden lg:flex flex-col w-72 border-r border-white/8 bg-[#0d0d0f] p-5 fixed inset-y-0">
        <div className="mb-8">{brand}</div>
        <nav className="flex-1 space-y-1">
          {NAV.map((n) => <SidebarLink key={n.to} {...n} />)}
        </nav>
        <div className="pt-4 border-t border-white/8">
          <div className="px-3.5 py-2 mb-1">
            <div className="text-sm text-white truncate">{client?.name}</div>
            <div className="text-xs text-[#71717A] truncate">{client?.company || client?.email}</div>
          </div>
          <button onClick={doLogout} data-testid="client-logout-btn"
            className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm text-[#A1A1AA] hover:text-white hover:bg-white/5">
            <LogOut className="w-[18px] h-[18px]" /> Sign out
          </button>
        </div>
      </aside>

      {/* mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 flex items-center justify-between px-4 h-16 bg-[#0d0d0f] border-b border-white/8">
        {brand}
        <button onClick={() => setOpen(true)} data-testid="client-menu-btn" className="text-white p-2"><Menu className="w-6 h-6" /></button>
      </div>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/70" onClick={() => setOpen(false)}>
          <div className="absolute left-0 inset-y-0 w-72 bg-[#0d0d0f] border-r border-white/8 p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-8">{brand}
              <button onClick={() => setOpen(false)} className="text-white p-1"><X className="w-5 h-5" /></button></div>
            <nav className="space-y-1">{NAV.map((n) => <SidebarLink key={n.to} {...n} onClick={() => setOpen(false)} />)}</nav>
            <button onClick={doLogout} className="w-full flex items-center gap-3 px-3.5 py-2.5 mt-4 rounded-xl text-sm text-[#A1A1AA] hover:text-white hover:bg-white/5">
              <LogOut className="w-[18px] h-[18px]" /> Sign out</button>
          </div>
        </div>
      )}

      <main className="flex-1 lg:ml-72 px-5 sm:px-8 py-8 pt-20 lg:pt-8 max-w-6xl w-full mx-auto">
        <Outlet />
      </main>
    </div>
  );
}
